import React from "react";

import { type RecipeVariantProps, cva, cx } from "../../styled-system/css";

export const buttonStyles = cva({
  base: {
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
    lineHeight: 1,
    paddingX: "16px",
    paddingY: "12px",
    textAlign: "center",
    transition: "all 0.2s ease-in-out",
    _disabled: {
      cursor: "not-allowed",
      opacity: 0.6,
    },
    _hover: {
      opacity: 0.8,
    },
  },
  defaultVariants: {
    color: "primary",
    variant: undefined,
  },
  variants: {
    color: {
      primary: {
        backgroundColor: "primary",
        color: "primary-foreground",
      },
      secondary: {
        backgroundColor: "secondary",
        color: "secondary-foreground",
      },
    },
    variant: {
      outline: {
        borderColor: "border",
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: "transparent",
        color: "primary",
      },
    },
  },
});

export const iconButtonStyles = cva({
  base: {
    alignItems: "center",
    aspectRatio: "1 / 1",
    border: "none",
    borderRadius: "65535px",
    cursor: "pointer",
    display: "inline-flex",
    justifyContent: "center",
    overflow: "clip",
    transition: "all 0.2s ease-in-out",
    width: "auto",
    _disabled: {
      cursor: "not-allowed",
      opacity: 0.6,
    },
    _hover: {
      opacity: 0.8,
    },
  },

  variants: {
    color: {
      ghost: {
        backgroundColor: "transparent",
        color: "foreground",
        border: "1px solid transparent",

        _hover: {
          backgroundColor: "muted-background",
          borderColor: "border",
        },
      },
    },
    size: {
      "40px": { height: "40px" },
    },
  },
});

export const ButtonStyle = cva({
  base: {
    alignItems: "center",
    cursor: "pointer",
    display: "inline-flex",
    fontSize: "14px",
    fontWeight: 500,
    justifyContent: "center",
    lineHeight: 1,
    transition: "all 0.2s ease-in-out",
    _disabled: {
      cursor: "not-allowed",
      opacity: 0.5,
    },
    _hover: {
      opacity: 0.7,
    },
  },

  defaultVariants: {
    color: "default",
  },

  variants: {
    appearance: {
      contained: {
        backgroundColor: "var(--background-color)",
        color: "var(--foreground-color)",
      },

      ghost: {
        backgroundColor: "transparent",
        color: "var(--foreground-color)",

        _hover: {
          backgroundColor: "var(--background-color)",
        },
      },

      outlined: {
        backgroundColor: "var(--background-color)",
        borderColor: "var(--border-color)",
        borderStyle: "solid",
        borderWidth: "1px",
        color: "var(--foreground-color)",
      },
    },

    borderRadius: {
      "4px": { borderRadius: "4px" },
      "6px": { borderRadius: "6px" },
      "100%": { borderRadius: "100%" },
    },

    color: {
      default: {
        "--background-color": "transparent",
        "--foreground-color": "colors.foreground",
      },
      ghost: {
        "--background-color": "colors.muted-background",
        "--foreground-color": "colors.foreground",
      },
      outlined: {
        "--background-color": "white",
        "--border-color": "#E4E4E780",
        "--foreground-color": "#18181B",
      },
      primary: {
        "--background-color": "colors.primary",
        "--foreground-color": "colors.primary-foreground",
      },
      secondary: {
        "--background-color": "colors.secondary",
        "--foreground-color": "colors.secondary-foreground",
      },
      white: {
        "--background-color": "white",
        "--border-color": "#E4E4E780",
        "--foreground-color": "#18181B",
      },
    },

    ellipsis: {
      true: { minWidth: 0, overflowX: "clip", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    },

    flexGrow: {
      1: { flexGrow: 1 },
    },

    fontSize: {
      "14px": { fontSize: "14px" },
    },

    gap: {
      "8px": { gap: "8px" },
    },

    icon: {
      true: {
        alignItems: "center",
        aspectRatio: "1 / 1",
        justifyContent: "center",
      },
    },

    lineHeight: {
      1: { lineHeight: 1 },
      "10 / 7": { lineHeight: /** 1.428571… */ 10 / 7 },
      "14 / 9": { lineHeight: /** 1.5… */ 14 / 9 },
    },

    minWidth: { 0: { minWidth: 0 } },

    padding: {
      "10px 16px": { padding: "10px 16px" },
      "12px": { padding: "12px" },
    },

    size: {
      "32px": { height: "32px", width: "32px" },
      "40px": { height: "40px", width: "40px" },
    },

    width: {
      stretch: { width: "stretch" },
    },
  },
});

/**
 * @deprecated 代わりに`ButtonStyle`を使用する。
 */
export const BUTTON_STYLES = ButtonStyle;

export type ButtonProperties = React.ButtonHTMLAttributes<HTMLButtonElement> &
  RecipeVariantProps<typeof buttonStyles>;

export function Button({ children, className, ...properties }: ButtonProperties) {
  return (
    <button className={cx(buttonStyles(), className)} {...properties}>
      {children}
    </button>
  );
}

export const ButtonStyle2 = cva({
  base: {
    borderRadius: "6px",
    borderStyle: "solid",
    borderWidth: "1px",
    cursor: "pointer",
    fontFamily: '"Noto Sans JP", "Noto Color Emoji", serif',
    fontSize: "0.875rem",
    fontWeight: 500,
    lineHeight: 10 / 7,
    minHeight: "2.5rem",
    padding: "8px 16px",
    textAlign: "center",
    transition: "filter 0.1s ease-in-out, transform 0.1s ease-in",
    _active: { transform: "scale(0.975)" },
    _disabled: { cursor: "not-allowed", opacity: 0.5 },

    "& [data-icon]": {
      display: "inline",
      height: 8 / 7 + "em",
      marginBottom: 2 / 7 + "em",
      width: "auto",
    },
    "& [data-left]": { marginRight: 4 / 7 + "em" },
    "& [data-right]": { marginLeft: 4 / 7 + "em" },
  },
  variants: {
    color: {
      black: {
        backgroundColor: "#18181B",
        borderColor: "#18181B",
        color: "#FAFAFA",
        _active: { filter: "brightness(0.5)" },
        _focus: { filter: "brightness(0.9)" },
        _hover: { filter: "brightness(0.75)" },
      },
      gray: {
        backgroundColor: "#F4F4F5",
        borderColor: "#F4F4F5",
        color: "#18181B",
        _active: { filter: "brightness(0.9)" },
        _focus: { filter: "brightness(0.975)" },
        _hover: { filter: "brightness(0.95)" },
      },
      white: {
        backgroundColor: "white",
        borderColor: "#E4E4E7",
        color: "#18181B",
        _active: { filter: "brightness(0.9)" },
        _focus: { filter: "brightness(0.975)" },
        _hover: { filter: "brightness(0.95)" },
      },
    },

    flexGrow: {
      true: { flexGrow: 1, minWidth: 0 },
    },

    marginTop: {
      "8px": { marginTop: "8px" },
    },

    width: {
      stretch: { width: "stretch" },
    },
  },
});
